/* MCP Test dialog (A-0008.07).
 *
 * Two phases: Discover → list tools from the MCP server (saved or transient
 * settings); pick one; edit JSON arguments; Invoke → display the result.
 *
 * Calls Mind config-mode endpoints via the BeX-server proxy
 * (`/cm/projecto/proxy` → `projecto/config/api/v1/mcp/{discover,invoke}`).
 */
import {
    useEffect, useState
} from 'react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle
} from 'shared/components/ui/dialog';
import {
    Button
} from 'shared/components/ui/button';
import {
    Label
} from 'shared/components/ui/label';
import {
    ScrollArea
} from 'shared/components/ui/scroll-area';
import {
    mcpDiscoveryService
} from 'shared/api/services/BEX/mcpDiscovery.service';

const _renderError = (err) => {
    const data = err?.response?.data;
    if (typeof data === 'string') return data;
    if (data) return JSON.stringify(data, null, 2);
    return err?.message || String(err);
};

const TestMCPDialog = ({
    open, onClose, orgId, subOrgId, connectionId, draft, isSaved
}) => {
    const [phase, setPhase] = useState('idle'); // idle | discovering | discovered | invoking | invoked
    const [tools, setTools] = useState([]);
    const [selectedTool, setSelectedTool] = useState(null);
    const [argsText, setArgsText] = useState('{}');
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');

    // Reset on close.
    useEffect(() => {
        if (!open) {
            setPhase('idle');
            setTools([]);
            setSelectedTool(null);
            setArgsText('{}');
            setResult(null);
            setError('');
        }
    }, [open]);

    const buildBasePayload = () => {
        // Saved → use connection_id; unsaved → send transient settings.
        if (isSaved && connectionId) {
            return {
                org_id: orgId,
                sub_org_id: subOrgId,
                connection_id: connectionId
            };
        }
        return {
            org_id: orgId,
            sub_org_id: subOrgId,
            settings: {
                server_url: draft.server_url,
                transport: draft.transport,
                timeout_ms: draft.timeout_ms,
                auth: draft.auth, // plaintext on transient — never persisted
                headers: draft.headers || {},
            },
        };
    };

    const handleDiscover = async () => {
        setError('');
        setPhase('discovering');
        try {
            const res = await mcpDiscoveryService.discover(buildBasePayload());
            setTools(res.tools || []);
            setPhase('discovered');
        } catch (err) {
            setError(_renderError(err));
            setPhase('idle');
        }
    };

    const handleInvoke = async () => {
        if (!selectedTool) return;
        setError('');
        setPhase('invoking');
        let parsed = {};
        try { parsed = JSON.parse(argsText || '{}'); } catch (e) {
            setError('arguments must be valid JSON: ' + e.message);
            setPhase('discovered');
            return;
        }
        try {
            const res = await mcpDiscoveryService.invoke({
                ...buildBasePayload(),
                tool_name: selectedTool.name,
                arguments: parsed,
            });
            setResult(res.result);
            setPhase('invoked');
        } catch (err) {
            setError(_renderError(err));
            setPhase('discovered');
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-3xl">
                <DialogHeader>
                    <DialogTitle>Test MCP Connection</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    {error && (
                        <pre className="text-xs text-destructive whitespace-pre-wrap break-words bg-destructive/10 p-2 rounded">
                            {String(error)}
                        </pre>
                    )}

                    {phase === 'idle' && (
                        <div>
                            <Button onClick={handleDiscover} disabled={!draft?.server_url}>
                                Discover tools
                            </Button>
                        </div>
                    )}

                    {phase === 'discovering' && (
                        <p className="text-sm text-muted-foreground">Connecting to MCP server…</p>
                    )}

                    {(phase === 'discovered' || phase === 'invoking' || phase === 'invoked') && (
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Tools ({tools.length})</Label>
                                <ScrollArea className="max-h-[300px] mt-2">
                                    <ul className="space-y-1">
                                        {tools.map((t) => (
                                            <li
                                                key={t.name}
                                                className={`p-2 rounded border cursor-pointer text-xs ${
                                                    selectedTool?.name === t.name ? 'border-primary' : 'border-border'
                                                }`}
                                                onClick={() => setSelectedTool(t)}
                                            >
                                                <p className="font-mono font-semibold">{t.name}</p>
                                                <p className="text-muted-foreground line-clamp-3">
                                                    {t.description}
                                                </p>
                                            </li>
                                        ))}
                                    </ul>
                                </ScrollArea>
                                <Button
                                    size="sm" variant="outline" className="mt-2"
                                    onClick={handleDiscover}
                                >Re-discover</Button>
                            </div>

                            <div className="space-y-2">
                                <Label>{selectedTool ? `Arguments — ${selectedTool.name}` : 'Pick a tool first'}</Label>
                                <textarea
                                    className="w-full h-32 font-mono text-xs p-2 border rounded resize-y"
                                    value={argsText}
                                    onChange={(e) => setArgsText(e.target.value)}
                                    disabled={!selectedTool}
                                    placeholder='{"q": "example query"}'
                                />
                                {selectedTool?.inputSchema && (
                                    <details className="text-xs text-muted-foreground">
                                        <summary>Input schema</summary>
                                        <pre className="mt-1 p-2 bg-muted rounded text-[10px] overflow-x-auto">
                                            {JSON.stringify(selectedTool.inputSchema, null, 2)}
                                        </pre>
                                    </details>
                                )}
                                <Button
                                    onClick={handleInvoke}
                                    disabled={!selectedTool || phase === 'invoking'}
                                >
                                    {phase === 'invoking' ? 'Invoking…' : 'Invoke'}
                                </Button>
                                {result !== null && (
                                    <div>
                                        <Label>Result</Label>
                                        <pre className="text-xs p-2 bg-muted rounded overflow-x-auto max-h-[200px]">
                                            {typeof result === 'string' ? result : JSON.stringify(result, null, 2)}
                                        </pre>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default TestMCPDialog;
